import { canadianProvinceCodeSet, COUNTRY_CODES, usStateCodeSet } from '@govinity/shared-config';
import { z } from 'zod';
// US Zip code regex (5 digits or 5+4 format)
const US_ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
// Canadian postal code regex (A1A 1A1 format)
const CA_POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const validateState = (data, options = { isRequired: false }) => {
    if (!data.state) {
        return !options.isRequired;
    }
    // Transform state to uppercase for US and Canada
    if (data.countryCode === 'US' || data.countryCode === 'CA') {
        data.state = data.state.toUpperCase();
    }
    if (data.countryCode === 'US') {
        return usStateCodeSet.has(data.state);
    }
    if (data.countryCode === 'CA') {
        return canadianProvinceCodeSet.has(data.state);
    }
    return true;
};
const validatePostalCode = (data, options = { isRequired: false }) => {
    if (!data.postalCode) {
        return !options.isRequired;
    }
    if (data.countryCode === 'US') {
        return US_ZIP_CODE_REGEX.test(data.postalCode);
    }
    if (data.countryCode === 'CA') {
        return CA_POSTAL_CODE_REGEX.test(data.postalCode);
    }
    return true;
};
// Enhanced schema with country-specific error messages
export const addressRequiredSchema = z
    .object({
    address1: z
        .string('Address is required')
        .min(1, 'Address is required')
        .describe('The first line of the address'),
    address2: z.string().nullish().describe('The second, optional, line of the address'),
    city: z.string('City is required').min(1, 'City is required'),
    state: z
        .string('State/Province/Region is required')
        .min(1, 'State/Province/Region is required'),
    postalCode: z.string('Postal code is required').min(1, 'Postal code is required'),
    countryCode: z
        .enum(COUNTRY_CODES, 'Country code is required')
        .describe('The country code of the address, use ISO 3166-1 alpha-2. Default to US'),
})
    .refine((data) => {
    return validateState(data, { isRequired: true });
}, {
    message: 'Please enter a valid state/province',
    path: ['state'],
})
    .refine((data) => {
    return validatePostalCode(data, { isRequired: true });
}, {
    message: 'Please enter a valid postal code',
    path: ['postalCode'],
});
export const addressOptionalSchema = z
    .object({
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    countryCode: z.enum(COUNTRY_CODES, 'Country code is required'),
})
    .superRefine((data, ctx) => {
    // Validate state format if provided
    if (data.state &&
        !validateState({ countryCode: data.countryCode, state: data.state }, { isRequired: false })) {
        ctx.addIssue({
            code: 'custom',
            message: 'Please enter a valid state/province',
            path: ['state'],
        });
    }
    // Validate postal code format if provided
    if (data.postalCode &&
        !validatePostalCode({ countryCode: data.countryCode, postalCode: data.postalCode }, { isRequired: false })) {
        ctx.addIssue({
            code: 'custom',
            message: 'Please enter a valid postal code',
            path: ['postalCode'],
        });
    }
});
